import { animated, useTransition } from '@react-spring/web'
import classNames from 'classnames'
import type { FunctionComponent } from 'react'
import type { LiteralUnion } from 'type-fest'

import type { TestId } from '../../lib/utils/testid'
import { Skeleton } from '../skeleton'
import styles from './receipt.module.scss'

/**
 * Receipt component, that displays a bunch of terms with their definitions.
 *
 * | ------- | ---------- |
 * | ------- | ---------- |
 * | __term1__ | definition |
 * | __term2__ | definition2 |
 * | __term3__ | definition3 |
 */
export const Receipt: FunctionComponent<Props> = ({
    justify = 'space-between',
    loading = false,
    size = 'medium',
    items = [],
}) => {
    const transition = useTransition(loading, {
        from: { opacity: 0, height: 0 },
        enter: { opacity: 1, height: 'auto' },
        leave: { opacity: 0, height: 0 },
    })

    const classes = {
        row: classNames(styles.row, styles[justify]),
        term: classNames(
            styles.term,
            styles[`term-${justify}`],
            styles[`term-${size}`],
        ),
        definition: classNames(
            styles.definition,
            styles[`definition-${justify}`],
        ),
    }

    return transition((style, item) => (
        <animated.dl className={styles.receipt} style={style}>
            {item
                ? Array.from(Array(3), (_, index) => (
                      <div className={classes.row} key={index}>
                          <dt className={classes.term}>
                              <Skeleton className={styles.skeleton} />
                          </dt>
                          <dd className={classes.definition}>
                              <Skeleton className={styles.skeleton} />
                          </dd>
                      </div>
                  ))
                : items.map(({ term, definition, testId }, index) => (
                      <div
                          className={classes.row}
                          key={`${index}-${term}`}
                          data-testid={testId as TestId}
                      >
                          <dt className={classes.term}>{term}</dt>
                          <dd className={classes.definition}>{definition}</dd>
                      </div>
                  ))}
        </animated.dl>
    ))
}

export type ReceiptItem = {
    /**
     * The term to display.
     */
    term?: string

    /**
     * Term's definition.
     */
    definition?: string

    /**
     * The test id for the row.
     */
    testId?: LiteralUnion<TestId, string>
}

type Props = {
    /**
     * List of items to display.
     */
    items?: ReceiptItem[]

    /**
     * If true, the component will display a skeleton.
     */
    loading?: boolean

    /**
     * Alignment of the receipt items.
     */
    justify?: 'center' | 'space-between'

    /**
     * Size of the component.
     * @default "medium"
     */
    size?: 'small' | 'medium'
}
